import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * The horizontal tab strip — what the desktop 260px sidebar becomes.
 *
 * Scrolling is the platform's: `overflow-x: auto` in the stylesheet, no touch
 * handlers here. Selecting is a tap. The only JavaScript is keeping the active
 * pill on screen, because a tab three off the right edge can be selected by a
 * deep link (`?section=`) or by the Overview card's "View all" cross-link, and
 * an active pill nobody can see reads as no selection at all.
 */
const MobileTabRail = ({ tabs, activeKey, onSelect }) => {
  const railRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    const rail = railRef.current;
    const pill = activeRef.current;
    if (!rail || !pill) return;

    /* scrollIntoView would also scroll the PAGE vertically to reach the rail,
       which is wrong when the selection changed without the user touching the
       rail. Adjust the rail's own scrollLeft instead, and only when the pill is
       actually out of view — otherwise every re-render nudges the strip. */
    const railBox = rail.getBoundingClientRect();
    const pillBox = pill.getBoundingClientRect();
    const pad = 16;

    if (pillBox.left < railBox.left + pad) {
      rail.scrollLeft -= railBox.left + pad - pillBox.left;
    } else if (pillBox.right > railBox.right - pad) {
      rail.scrollLeft += pillBox.right - (railBox.right - pad);
    }
  }, [activeKey]);

  return (
    <nav className="zym-tabs" aria-label="Patient record sections" ref={railRef}>
      {tabs.map((t) => {
        const active = t.key === activeKey;
        return (
          <button
            key={t.key}
            ref={active ? activeRef : null}
            type="button"
            className={`zym-tab${active ? " is-active" : ""}`}
            aria-current={active ? "true" : undefined}
            onClick={() => onSelect(t.key)}
          >
            {t.label}
          </button>
        );
      })}
    </nav>
  );
};

MobileTabRail.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default MobileTabRail;
